import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import { Loader2, ShieldCheck, CreditCard, AlertCircle } from 'lucide-react';
import logoImage from '/assets/images/logo-volaptic-1775131861970.png';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
      iconColor: '#32b3e5',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// ─── Card Payment Form (inner component, needs Elements context) ───────────────
const CardPaymentForm = ({ clientSecret, amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setCardError(null);

    const cardElement = elements?.getElement(CardElement);

    const { error, paymentIntent } = await stripe?.confirmCardPayment(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (error) {
      setCardError(error?.message || 'Pagamento non riuscito.');
      onError(error?.message);
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
    } else {
      const msg = `Stato pagamento: ${paymentIntent?.status}`;
      setCardError(msg);
      onError(msg);
      setIsProcessing(false);
    }
  };

  const formattedAmount = amount
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(amount / 100)
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount display */}
      {formattedAmount && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">Totale da pagare</span>
          <span className="text-2xl font-bold" style={{ color: '#013776' }}>{formattedAmount}</span>
        </div>
      )}

      {/* Card input */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Dati carta di credito / debito
        </label>
        <div className="border border-gray-300 rounded-xl p-4 bg-white focus-within:border-[#32b3e5] focus-within:ring-2 focus-within:ring-[#32b3e5]/20 transition-all">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {/* Inline error */}
      {cardError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{cardError}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-3.5 px-6 rounded-xl font-semibold text-white text-base transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ background: isProcessing ? '#64b5d9' : 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)' }}
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Elaborazione in corso…
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5" />
            Paga ora
          </>
        )}
      </button>

      <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5" />
        Pagamento sicuro e crittografato da Stripe
      </p>
    </form>
  );
};

// ─── Klarna Trigger (inner component, needs Elements context) ──────────────────
const KlarnaPaymentTrigger = ({ clientSecret, userEmail, amount, onError }) => {
  const stripe = useStripe();
  const [isProcessing, setIsProcessing] = useState(false);
  const [triggered, setTriggered] = useState(false);

  const formattedAmount = amount
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(amount / 100)
    : null;

  const handleKlarna = useCallback(async () => {
    if (!stripe || triggered) return;
    setTriggered(true);
    setIsProcessing(true);

    const { error } = await stripe?.confirmKlarnaPayment(clientSecret, {
      payment_method: {
        billing_details: { email: userEmail },
      },
      return_url: window.location?.origin + '/cliente/checkout/conferma',
    });

    if (error) {
      onError(error?.message || 'Klarna non disponibile.');
      setIsProcessing(false);
      setTriggered(false);
    }
    // On success Stripe redirects automatically — no further action needed
  }, [stripe, clientSecret, userEmail, triggered, onError]);

  return (
    <div className="space-y-6">
      {formattedAmount && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">Totale da pagare</span>
          <span className="text-2xl font-bold" style={{ color: '#013776' }}>{formattedAmount}</span>
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600 leading-relaxed">
        Verrai reindirizzato a <strong>Klarna</strong> per completare il pagamento in modo sicuro.
        Dopo la conferma tornerai automaticamente su Volaptic.
      </div>

      <button
        onClick={handleKlarna}
        disabled={!stripe || isProcessing}
        className="w-full py-3.5 px-6 rounded-xl font-semibold text-white text-base transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ background: isProcessing ? '#64b5d9' : 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)' }}
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Reindirizzamento a Klarna…
          </>
        ) : (
          'Paga con Klarna'
        )}
      </button>

      <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5" />
        Pagamento sicuro e crittografato da Stripe
      </p>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ClienteCheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const quoteId = searchParams?.get('quote_id');
  const paymentMethod = searchParams?.get('payment_method'); // "card" | "klarna"

  const [isLoading, setIsLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Call Edge Function on mount
  useEffect(() => {
    // Wait for auth to finish loading before proceeding
    if (authLoading) return;

    const initPayment = async () => {
      if (!user) {
        setErrorMessage('Devi essere autenticato per procedere al pagamento.');
        setIsLoading(false);
        return;
      }

      if (!quoteId || !paymentMethod) {
        setErrorMessage('Parametri mancanti. Torna al preventivo e riprova.');
        setIsLoading(false);
        return;
      }

      try {
        const { data: { session } } = await supabase?.auth?.getSession();

        if (!session) {
          setErrorMessage('Sessione scaduta. Effettua di nuovo il login.');
          setIsLoading(false);
          return;
        }

        const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            quote_id: quoteId,
            payment_method_type: paymentMethod,
          }),
        });

        if (!response?.ok) {
          const errData = await response?.json()?.catch(() => ({}));
          throw new Error(errData?.error || errData?.message || `Errore server: ${response.status}`);
        }

        const data = await response?.json();
        setClientSecret(data?.client_secret);
        setAmount(data?.amount);
      } catch (err) {
        setErrorMessage(err?.message || 'Impossibile avviare il pagamento. Riprova più tardi.');
      } finally {
        setIsLoading(false);
      }
    };

    initPayment();
  }, [authLoading, user, quoteId, paymentMethod]);

  const handleSuccess = () => {
    navigate('/cliente/dashboard?success=payment');
  };

  const handleError = (msg) => {
    setErrorMessage(msg);
  };

  const methodLabel = paymentMethod === 'klarna' ? 'Klarna' : 'Carta di credito';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Minimal header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <a href="/">
          <img src={logoImage} alt="Volaptic" style={{ height: '48px', width: 'auto' }} />
        </a>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5 text-[#32b3e5]" />
          Pagamento sicuro
        </span>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #32b3e5 0%, #013776 100%)' }} />

            <div className="p-6 sm:p-8">
              <h1 className="text-xl font-bold mb-1" style={{ color: '#013776' }}>
                Completa il pagamento
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                Metodo selezionato: <span className="font-medium text-gray-700">{methodLabel}</span>
              </p>

              {/* Loading state */}
              {isLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#32b3e5' }} />
                  <p className="text-sm text-gray-500">Preparazione del pagamento…</p>
                </div>
              )}

              {/* Error state */}
              {!isLoading && errorMessage && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-700 mb-1">Errore</p>
                    <p className="text-sm text-red-600">{errorMessage}</p>
                    <button
                      onClick={() => navigate(-1)}
                      className="mt-3 text-xs font-medium underline text-red-500 hover:text-red-700"
                    >
                      ← Torna indietro
                    </button>
                  </div>
                </div>
              )}

              {/* Payment forms */}
              {!isLoading && !errorMessage && clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  {paymentMethod === 'card' ? (
                    <CardPaymentForm
                      clientSecret={clientSecret}
                      amount={amount}
                      onSuccess={handleSuccess}
                      onError={handleError}
                    />
                  ) : (
                    <KlarnaPaymentTrigger
                      clientSecret={clientSecret}
                      userEmail={user?.email}
                      amount={amount}
                      onError={handleError}
                    />
                  )}
                </Elements>
              )}
            </div>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-5">
            Hai bisogno di aiuto?{' '}
            <a href="mailto:supporto@volaptic.com" className="underline hover:text-gray-600">
              Contatta il supporto
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ClienteCheckoutPage;
